// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@keller/App/Components/Notify";
import { XngAddAccountParameter } from "@keller/App/Handlers/CreateAccount/AddAccount";
import loadCreateAccountFormInfo from "@keller/App/Handlers/CreateAccount/LoadCreateAccountFormInfo";
import CreateAccountApplyPurchasableForm from "@keller/App/Widgets/KscCreateAccount/CreateAccount.ApplyPurchasableForm";
import {
    AccountFormState,
    initialAccountFormState,
} from "@keller/App/Widgets/KscCreateAccount/CreateAccount.form.module";
import { AccountFormDate } from "@keller/App/Widgets/KscCreateAccount/CreateAccount.module";
import CreateAccountUserForm from "@keller/App/Widgets/KscCreateAccount/CreateAccount.UserForm";
import { color } from "@keller/XDL/Utilities";
import { CreateForm } from "@keller/XDL/Utilities/form";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, RefObject, useContext, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    addAccount,
    signIn,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
    loadCreateAccountFormInfo,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    centerWrapper?: InjectableCss;
    errorWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userSupportGridItem?: GridItemProps;
    createAccountGridItem?: GridItemProps;
    createAccountTitle?: TypographyProps;
    createAccountSubTitle?: TypographyProps;
    form?: InjectableCss;
    separatorGridItem?: GridItemProps;
    separator?: InjectableCss;
    subscriptionGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    notifyError?: TypographyPresentationProps;
    submitGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
}

export const createAccountStyles: CreateAccountStyles = {
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            min-height: 300px;
        `,
    },
    mainGridContainer: {
        gap: 44,
    },
    userSupportGridItem: {
        width: [12, 12, 12, 3, 3],
    },
    createAccountGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            flex-direction: column;
        `,
    },
    createAccountTitle: {
        size: 24,
        weight: 900,
        css: css`
            display: block;
            letter-spacing: 0;
            line-height: 30px;
            margin-bottom: 16px;
        `,
    },
    createAccountSubTitle: {
        size: 15,
        css: css`
            display: block;
            letter-spacing: 0;
            line-height: 24px;
            margin-bottom: 13px;
        `,
    },
    separatorGridItem: {
        width: 12,
        css: css`
            padding-top: 5px;
            padding-bottom: 5px;
        `,
    },
    separator: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            width: 100%;
        `,
    },
    subscriptionGridItem: {
        width: 12,
    },
    reCaptchaGridItem: {
        width: 12,
        css: css`
            &:empty {
                display: none;
            }
        `,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitGridItem: {
        width: 12,
        css: css`
            padding-top: 25px;
        `,
    },
    notifyError: {
        css: css`
            margin: 0;
            width: 100%;
        `,
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

const KellerCreateAccount: FC<Props> = ({
    addAccount,
    signIn,
    validateReCaptcha,
    accountSettings,
    returnUrl,
    id,
    loadCreateAccountFormInfo,
}) => {
    const [accountData, setAccountData] = useState<AccountFormDate>({ isLoading: false });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const toasterContext = useContext(ToasterContext);
    const formRef = useRef<HTMLFormElement>(null);
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    const [accountFormState, setAccountFormState] = useState<AccountFormState>(cloneDeep(initialAccountFormState));
    const accountForm = new CreateForm<AccountFormState>(accountFormState, setAccountFormState, {
        accountSettings,
    });

    useEffect(() => {
        loadAccountDate();
    }, []);

    const loadAccountDate = () => {
        setAccountData({ isLoading: true });
        loadCreateAccountFormInfo({
            onSuccess: result => {
                setAccountData({
                    data: result,
                    isLoading: false,
                    error: undefined,
                });
            },
            onError: error => {
                setAccountData({
                    data: undefined,
                    isLoading: false,
                    error,
                });
            },
        });
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
        const { email, userName, password } = accountForm.state.fields;
        setTimeout(() => {
            signIn({
                userName: accountSettings.useEmailAsUserName ? email.value : userName.value,
                password: password.value,
                rememberMe: false,
                returnUrl,
            });
        }, 500);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const { isSubmitted } = accountForm.state;

        if (!isSubmitted) {
            accountForm.setSubmitted();
        }

        if (isSubmitting) {
            return;
        }

        if (!accountForm.validate() || !formRef.current) {
            return;
        }

        if (!formRef.current) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "KellerCreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);

        const formData = new FormData(formRef.current);
        if (!formData) {
            return;
        }

        formData.append("defaultWarehouseId", accountForm.state.fields.defaultWarehouseId.value || "");
        formData.append("applyForPurchasable", `${accountForm.state.fields.applyForPurchasable.value}` || "false");

        if (accountForm.state.fields.applyForPurchasable.value) {
            formData.delete(accountForm.state.fields.stayInTouchByPrint.attrs!.name!);
            formData.delete(accountForm.state.fields.stayInTouchByOnline.attrs!.name!);
            const stayInTouchByPrint = accountForm.state.fields.stayInTouchByPrint.value;
            const stayInTouchByOnline = accountForm.state.fields.stayInTouchByOnline.value;
            let stayInTouch = "";
            if (stayInTouchByPrint) {
                stayInTouch = "print";
            }
            if (stayInTouchByOnline) {
                stayInTouch = "online";
            }
            if (stayInTouchByPrint && stayInTouchByOnline) {
                stayInTouch = "both";
            }
            formData.append("stayInTouch", stayInTouch);
        }

        if (!accountForm.state.fields.creditApplicationFile.value) {
            formData.delete(accountForm.state.fields.creditApplicationFile.attrs!.name!);
        }

        addAccount({
            email: "",
            userName: "",
            password: "",
            formData,
            isSubscribed: false,
            onSuccess: onSuccessHandler,
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as XngAddAccountParameter);
    };

    if (accountData.isLoading || !accountForm) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (accountData.error) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Notify type="danger">{accountData.error}</Notify>
            </StyledWrapper>
        );
    }

    const { fields } = accountForm.state;

    const isFormValid = accountForm.validate();
    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.userSupportGridItem}>
                <Zone zoneName="User Support" contentId={id} />
            </GridItem>
            <GridItem {...styles.createAccountGridItem}>
                <StyledWrapper>
                    <Typography {...styles.createAccountTitle}>{translate("Create a New Account")}</Typography>
                    <Typography {...styles.createAccountSubTitle}>
                        {translate("Please create an account to start a wishlist.")}
                    </Typography>
                </StyledWrapper>
                <StyledForm {...styles.form} onSubmit={submitHandler} noValidate ref={formRef}>
                    <GridContainer gap={22}>
                        <CreateAccountUserForm accountData={accountData.data} accountForm={accountForm} />
                        <GridItem {...styles.separatorGridItem}>
                            <StyledWrapper {...styles.separator} />
                        </GridItem>
                        <GridItem {...styles.subscriptionGridItem}>
                            <CheckboxGroup>
                                <Checkbox
                                    onChange={(e, value) => {
                                        accountForm?.onFieldChange(fields.applyForPurchasable, value);
                                        accountForm.setPrestine();
                                    }}
                                    checked={fields.applyForPurchasable.value}
                                    data-test-selector="createAccount_Subscription"
                                >
                                    {siteMessage("Keller_Apply_For_Purchasable_Account")}
                                </Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                        <CreateAccountApplyPurchasableForm accountData={accountData.data} accountForm={accountForm} />
                        <GridItem {...styles.reCaptchaGridItem}>
                            <ReCaptcha location="CreateAccount" recaptchaRef={recaptchaRef} />
                        </GridItem>
                        {errorMessage && (
                            <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                                <Notify type="danger" {...styles.notifyError}>
                                    {errorMessage}
                                </Notify>
                            </GridItem>
                        )}
                        <GridItem {...styles.submitGridItem}>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isSubmitting || (!isFormValid && accountForm.state.isSubmitted)}
                                data-test-selector="createAccount_createButton"
                            >
                                {isSubmitting ? translate("Creating ...") : translate("Create My Account")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(KellerCreateAccount),
    definition: {
        displayName: "Keller - Create Account",
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
    },
};

export default widgetModule;
